import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription, hasTier } from '../context/SubscriptionContext';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import TierGate from '../components/TierGate';
import TradingViewWidget from '../components/TradingViewWidget';
import { externalApi } from '../services/api';

import { StockHeader, PriceHeader } from './stock-detail/StockHeader';
import ChartToolbar from './stock-detail/ChartToolbar';
import { PriceChart, RSIMACDPanels } from './stock-detail/Charts';
import PatternResult from './stock-detail/PatternResult';
import { FundamentalsCard, TvRatingCard } from './stock-detail/ExternalCards';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PATTERN_ERROR = {
  pattern: 'Analysis failed', trend: 'unknown', recommendation: 'hold',
  rationale: 'API error - try again', confidence: 0,
};

function buildChartData(chart) {
  return chart.candles.map((c, i) => ({
    date: c.date,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume,
    sma20: chart.sma_20[i],
    sma50: chart.sma_50[i],
    ema9: chart.ema_9[i],
    bollUpper: chart.bollinger.upper[i],
    bollLower: chart.bollinger.lower[i],
    rsi: chart.rsi[i],
    macd: chart.macd.macd[i],
    macdSignal: chart.macd.signal[i],
    macdHist: chart.macd.histogram[i],
    range: [c.low, c.high],
    body: [Math.min(c.open, c.close), Math.max(c.open, c.close)],
  }));
}

export default function StockDetail() {
  const { user, loading } = useAuth();
  const { tier, loading: subLoading } = useSubscription();
  const { symbol } = useParams();
  const navigate = useNavigate();

  const [stock, setStock] = useState(null);
  const [chart, setChart] = useState(null);
  const [timeframe, setTimeframe] = useState('1D');
  const [activeIndicators, setActiveIndicators] = useState(['SMA20', 'Bollinger']);
  const [pattern, setPattern] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [fundamentals, setFundamentals] = useState(null);
  const [tvRating, setTvRating] = useState(null);

  const loadExternalData = useCallback(async () => {
    const safe = (p) => p.catch((e) => { console.error('external fetch failed', e); return { data: null }; });
    const [f, tv] = await Promise.all([
      safe(externalApi.screener(symbol)),
      safe(externalApi.tradingview(symbol)),
    ]);
    setFundamentals(f.data);
    setTvRating(tv.data);
  }, [symbol]);

  useEffect(() => { if (symbol) loadExternalData(); }, [symbol, loadExternalData]);

  const loadData = useCallback(async () => {
    if (!hasTier(tier, 'pro')) return;
    try {
      const [stockRes, chartRes] = await Promise.all([
        axios.get(`${API}/stocks/${symbol}`, { withCredentials: true }),
        axios.get(`${API}/stocks/${symbol}/chart`, { params: { timeframe }, withCredentials: true }),
      ]);
      setStock(stockRes.data);
      setChart(chartRes.data);
    } catch (e) {
      console.error('stock chart fetch failed', e);
      setStock(null);
    }
  }, [symbol, timeframe, tier]);

  useEffect(() => {
    if (user && hasTier(tier, 'pro')) loadData();
  }, [user, tier, loadData]);

  const runPatternDetection = async () => {
    if (!hasTier(tier, 'elite')) {
      navigate('/pricing?target=elite&from=' + encodeURIComponent(window.location.pathname));
      return;
    }
    setDetecting(true);
    setPattern(null);
    try {
      const { data } = await axios.post(`${API}/ai/pattern-detect`, { symbol }, { withCredentials: true });
      setPattern(data);
    } catch (e) {
      console.error('pattern detect failed', e);
      setPattern(PATTERN_ERROR);
    } finally {
      setDetecting(false);
    }
  };

  const toggleIndicator = (ind) => {
    setActiveIndicators((prev) => (prev.includes(ind) ? prev.filter((i) => i !== ind) : [...prev, ind]));
  };

  if (loading || subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0B0E' }}>
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;

  if (!hasTier(tier, 'pro')) {
    return (
      <Layout>
        <TierGate requiredTier="pro" feature="Stock Charts & Technical Analysis">
          <div />
        </TierGate>
      </Layout>
    );
  }
  if (!stock || !chart) {
    return <Layout><div className="p-8 text-center text-[#94A3B8]">Loading {symbol}...</div></Layout>;
  }

  const chartData = buildChartData(chart);

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-6" data-testid="stock-detail-page">
        <StockHeader stock={stock} onBack={() => navigate(-1)} />
        <PriceHeader stock={stock} />

        <ChartToolbar
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          activeIndicators={activeIndicators}
          onToggleIndicator={toggleIndicator}
          onRunPattern={runPatternDetection}
          detecting={detecting}
        />

        <PriceChart chartData={chartData} activeIndicators={activeIndicators} pattern={pattern} />
        <RSIMACDPanels chartData={chartData} chart={chart} />

        {pattern && <PatternResult pattern={pattern} />}

        {(fundamentals || tvRating) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <FundamentalsCard fundamentals={fundamentals} />
            <TvRatingCard tvRating={tvRating} />
          </div>
        )}

        <div className="bg-[#12141A] border border-white/10 mt-6" data-testid="stock-tv-widget">
          <div className="px-5 pt-4 pb-2 border-b border-white/5">
            <h3 className="text-sm font-heading font-bold text-white">Live TradingView Chart</h3>
            <p className="text-[11px] text-[#64748B]">Interactive chart from TradingView (free embed)</p>
          </div>
          <TradingViewWidget symbol={symbol} exchange="NSE" height={480} />
        </div>
      </div>
    </Layout>
  );
}
